import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import axios from 'axios';
import ContactFormModal from '../components/ContactFormModal';
import { useAuth } from '../context/AuthContext';
import { PageSEO } from '../components/SEO';
import { BACKEND_URL } from '../config';
import { trackPlanSelected } from '../utils/analytics';

// Phase 1 (ADR-0009 §2): two plans, prices come from the subscription_tiers rows —
// the same rows Stripe charges and the deal/venue caps enforce. Nothing is hardcoded.
export const SELECTED_TIER_KEY = 'tappd_selected_tier';

const FAQ = [
  { q: 'How does Tappd work?', a: 'Partner venues (bars and restaurants) put Tappd coasters on their tables. A patron scans the QR code, sees the deals from local businesses at that venue, claims one, and shows the code at your counter.' },
  { q: 'What is a "venue"?', a: 'A partner bar or restaurant that hosts Tappd coasters. The Local plan puts your deal at one venue; the Multi plan lets you pick up to three.' },
  { q: 'Can I change or cancel my plan?', a: 'Yes. From your dashboard you can open the billing portal to switch between Local and Multi, update your card, or cancel. Cancelling stops your deals at the end of the billing period.' },
  { q: 'What happens after I subscribe?', a: 'Our team reviews your listing (usually within one business day) and then your deal goes live at the venues you picked.' },
  { q: 'Is there a contract?', a: 'No. Monthly plans renew month to month. Annual plans are billed once a year and include two months free.' },
];

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [pendingPlanTier, setPendingPlanTier] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const { isAuthenticated, openAuthModal, user } = useAuth();
  const hasContinued = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/subscription-tiers`);
        const list = (res.data?.tiers || [])
          .filter(t => t.is_public !== false && t.is_enabled !== false)
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        if (!cancelled) setTiers(list);
      } catch (e) {
        console.error('[Pricing] failed to load plans', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const continueWithPlan = (tierKey) => {
    localStorage.setItem(SELECTED_TIER_KEY, tierKey);
    const alreadyAdvertiser = user?.business_id || user?.role === 'advertiser' || user?.account_type === 'advertiser';
    if (alreadyAdvertiser) {
      navigate(`/business/subscription?plan=${tierKey}`);
    } else {
      navigate('/business/onboarding');
    }
  };

  // After signing in from the plan button, continue where the user left off
  useEffect(() => {
    if (isAuthenticated && pendingPlanTier && !hasContinued.current) {
      hasContinued.current = true;
      continueWithPlan(pendingPlanTier);
    }
  }, [isAuthenticated, pendingPlanTier]);

  const handlePlanClick = (tierKey) => {
    trackPlanSelected({ planTier: tierKey });
    if (!isAuthenticated) {
      setPendingPlanTier(tierKey);
      localStorage.setItem(SELECTED_TIER_KEY, tierKey);
      openAuthModal('signup');
      return;
    }
    continueWithPlan(tierKey);
  };

  const priceFor = (t) => billingCycle === 'monthly' ? t.monthly_price : t.annual_price;
  const perMonth = (t) => billingCycle === 'monthly' ? t.monthly_price : (t.annual_price / 12);
  const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: n % 1 ? 2 : 0 });

  return (
    <>
      <PageSEO.Pricing />
      <div className="min-h-screen bg-[#FEF7ED] text-[#1e1b4b] overflow-x-hidden">
        {/* Hero */}
        <section className="pt-32 pb-10 px-6 text-center">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs font-mono tracking-widest text-[#8b5cf6] mb-4">// pricing</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-4xl sm:text-5xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Simple pricing. Real customers.
          </motion.h1>
          <p className="max-w-2xl mx-auto text-[#1e1b4b]/70 text-lg">
            Your deal on Tappd coasters at the venues you choose. Pick a plan, add your deal, and we'll get you live.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center mt-8 p-1 rounded-full bg-white border border-[#1e1b4b]/10 shadow-sm">
            {['monthly', 'annual'].map(cycle => (
              <button
                key={cycle}
                onClick={() => setBillingCycle(cycle)}
                className="px-5 py-2 rounded-full text-sm font-semibold transition-all"
                style={billingCycle === cycle ? { background: 'linear-gradient(135deg, #f97316, #ea580c)', color: 'white' } : { color: '#1e1b4b' }}
              >
                {cycle === 'monthly' ? 'Monthly' : 'Annual · 2 months free'}
              </button>
            ))}
          </div>
        </section>

        {/* Plans */}
        <section className="px-6 pb-16">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin" size={36} style={{ color: '#8b5cf6' }} /></div>
          ) : tiers.length === 0 ? (
            <p className="text-center text-[#1e1b4b]/60">Plans are being set up. <button className="underline" onClick={() => setIsContactModalOpen(true)}>Contact us</button> and we'll get you started.</p>
          ) : (
            <div className="max-w-4xl mx-auto grid gap-6 md:grid-cols-2">
              {tiers.map((t, i) => {
                const highlight = !!t.badge_text;
                return (
                  <motion.div
                    key={t.tier_key}
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                    className="relative bg-white rounded-3xl p-8 border shadow-sm flex flex-col"
                    style={{ borderColor: highlight ? 'rgba(139, 92, 246, 0.5)' : 'rgba(30, 27, 75, 0.08)', boxShadow: highlight ? '0 20px 50px rgba(139, 92, 246, 0.15)' : undefined }}
                  >
                    {t.badge_text && (
                      <span className="absolute -top-3 left-8 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}>{t.badge_text}</span>
                    )}
                    <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{t.name}</h2>
                    <p className="text-[#1e1b4b]/60 text-sm mb-6">{t.description}</p>
                    <div className="mb-1">
                      <span className="text-5xl font-bold">${fmt(perMonth(t))}</span>
                      <span className="text-[#1e1b4b]/60"> /month</span>
                    </div>
                    <p className="text-xs text-[#1e1b4b]/50 mb-3 h-4">
                      {billingCycle === 'annual' ? `Billed $${fmt(priceFor(t))} per year` : 'Billed monthly, cancel anytime'}
                    </p>
                    {(t.hub_cap || t.deal_limit) && (
                      <p className="text-sm font-semibold text-[#1e1b4b] mb-6">
                        {[
                          t.hub_cap ? (t.hub_cap === 1 ? '1 venue' : `Up to ${t.hub_cap} venues`) : null,
                          t.deal_limit ? (t.deal_limit === 1 ? '1 active deal' : `${t.deal_limit} active deals`) : null,
                        ].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    <ul className="space-y-3 mb-8 flex-1">
                      {(t.features || []).map((f, idx) => (
                        <li key={idx} className="flex items-start gap-3 text-sm">
                          <span className="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(34, 197, 94, 0.12)' }}>
                            <Check size={12} className="text-green-600" />
                          </span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => handlePlanClick(t.tier_key)}
                      className="w-full py-3.5 rounded-xl font-bold text-white transition-all hover:-translate-y-0.5"
                      style={{ background: highlight ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)' : 'linear-gradient(135deg, #f97316, #ea580c)', boxShadow: '0 8px 24px rgba(30, 27, 75, 0.15)' }}
                    >
                      Get started with {t.name}
                    </button>
                  </motion.div>
                );
              })}
            </div>
          )}
          <p className="text-center text-sm text-[#1e1b4b]/60 mt-8">
            Questions, or need more than three venues? <button className="underline font-semibold" onClick={() => setIsContactModalOpen(true)}>Talk to us</button>.
          </p>
        </section>

        {/* FAQ */}
        <section className="px-6 pb-24">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Common questions</h2>
            <div className="space-y-3">
              {FAQ.map((item, idx) => (
                <div key={idx} className="bg-white rounded-2xl border border-[#1e1b4b]/8 overflow-hidden">
                  <button
                    id={`faq-q-${idx}`}
                    aria-expanded={openFaq === idx}
                    aria-controls={`faq-a-${idx}`}
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left font-semibold min-h-[44px]"
                  >
                    {item.q}
                    <ChevronDown size={18} aria-hidden="true" className={`transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === idx && (
                    <p id={`faq-a-${idx}`} role="region" aria-labelledby={`faq-q-${idx}`} className="px-6 pb-5 text-sm text-[#1e1b4b]/70 leading-relaxed">{item.a}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
      <ContactFormModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </>
  );
};

export default Pricing;
